import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { startCheckout, startSubscription } from "@/lib/pay";
import { KEY_MOMENTS } from "@/pages/Landing";
import { Check } from "lucide-react";

const BG_LANDLORD = "https://images.unsplash.com/photo-1758523671893-0ba21cf4260f?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";

const landlordPlans = [
  { id: "landlord_starter", name: "Starter", price: 15, unit: "5 matches", features: ["5 tenant matches (offers)", "Browse verified tenants", "In-app messaging", "Credits never expire"] },
  { id: "landlord_growth", name: "Growth", price: 35, unit: "20 matches", popular: true, features: ["20 tenant matches (offers)", "Advanced filters", "Featured placement", "Property auto-matching"] },
  { id: "landlord_unlimited", name: "Unlimited", price: 50, unit: "per month", features: ["Unlimited matches for 30 days", "Everything in Growth", "Priority support", "Best for active portfolios"] },
];

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const choose = (id) => {
    if (!user) { localStorage.setItem("rm_pending_role", "landlord"); navigate("/auth?role=landlord"); return; }
    if (id === "landlord_unlimited") { startSubscription(); return; }
    startCheckout(id);
  };

  return (
    <div className="relative min-h-screen overflow-hidden">
      <div aria-hidden className="absolute inset-0 z-0">
        <img src={BG_LANDLORD} alt="" className="w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-background/85" />
      </div>
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 md:px-8 py-16 relative z-10">
        <div className="text-center mb-12">
          <img src={KEY_MOMENTS[1].url} alt={KEY_MOMENTS[1].alt} className="w-20 h-20 rounded-full object-cover mx-auto mb-4 border-4 border-white shadow-sm" />
          <p className="overline text-primary">Landlord plans</p>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tighter font-bold mt-2">Pay for outcomes, not listings</h1>
          <p className="text-muted-foreground mt-3">Tenants verify for a one-time fee. Landlords subscribe by offer volume.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {landlordPlans.map((p) => (
            <div key={p.id} data-testid={`plan-${p.id}`} className={`bg-white border rounded-sm p-8 flex flex-col ${p.popular ? "border-primary ring-1 ring-primary" : "border-border"}`}>
              {p.popular && <span className="overline text-primary mb-2">Most popular</span>}
              <h3 className="font-display text-2xl font-semibold">{p.name}</h3>
              <div className="my-4"><span className="font-display text-4xl font-bold">${p.price}</span><span className="text-muted-foreground"> · {p.unit}</span></div>
              <ul className="space-y-2 mb-8 flex-1">
                {p.features.map((f) => (<li key={f} className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-emerald-600" />{f}</li>))}
              </ul>
              <Button data-testid={`choose-${p.id}`} variant={p.popular ? "default" : "outline"} onClick={() => choose(p.id)}>Choose {p.name}</Button>
            </div>
          ))}
        </div>
        <div className="text-center mt-12 text-sm text-muted-foreground">
          Tenants: one-time <b>$24.99</b> verification · <b>$10</b> reactivation when you re-enter the pool.<br />
          <span className="text-emerald-700 font-semibold">🎉 The first 100 tenants verify FREE.</span><br />
          <button className="text-primary underline underline-offset-2 mt-2" data-testid="link-tenant-pricing" onClick={() => navigate("/tenant-pricing")}>See full tenant pricing →</button>
        </div>
      </div>
    </div>
  );
}
